import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import {
  Users,
  Plus,
  Search,
  Upload,
  FileDown,
  Edit,
  Trash2,
  Mail,
  Phone,
  Building,
  MoreVertical,
  RefreshCw,
  Check,
  X,
  Target,
  List,
  UserPlus,
  PhoneCall,
  MessageSquare,
  Calendar,
  TrendingUp,
  FolderOpen,
  ArrowRight,
  History,
  Filter,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ProspectionPage() {
  const { token, isAdmin, isBureau } = useAuth();
  const [loading, setLoading] = useState(true);
  const [prospects, setProspects] = useState([]);
  const [listes, setListes] = useState([]);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState("prospects");
  
  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [filterListe, setFilterListe] = useState("all");
  const [filterStatut, setFilterStatut] = useState("all");
  
  // Dialogs
  const [showCreateProspectDialog, setShowCreateProspectDialog] = useState(false);
  const [showCreateListeDialog, setShowCreateListeDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  
  // Selection
  const [selectedProspects, setSelectedProspects] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Editing
  const [editingProspect, setEditingProspect] = useState(null);
  const [selectedProspectForContact, setSelectedProspectForContact] = useState(null);
  const [selectedProspectForHistory, setSelectedProspectForHistory] = useState(null);
  
  // Forms
  const [prospectForm, setProspectForm] = useState({
    nom: "", prenom: "", email: "", telephone: "",
    entreprise: "", poste: "", ville: "", source: "", notes: ""
  });
  const [listeForm, setListeForm] = useState({ nom: "", description: "", source: "" });
  const [contactForm, setContactForm] = useState({ type_contact: "appel", notes: "", resultat: "" });
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [prospectsRes, listesRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/prospection/prospects`, { headers }),
        axios.get(`${API}/api/prospection/listes`, { headers }),
        axios.get(`${API}/api/prospection/prospects/stats`, { headers }),
      ]);
      setProspects(prospectsRes.data);
      setListes(listesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  // Filter prospects
  const filteredProspects = prospects.filter(p => {
    const matchSearch = !searchTerm || 
      p.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.prenom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.entreprise?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.telephone?.includes(searchTerm);
    
    const matchListe = filterListe === "all" || p.liste_id === filterListe;
    const matchStatut = filterStatut === "all" || p.statut === filterStatut;
    
    return matchSearch && matchListe && matchStatut;
  });

  // Status badge
  const getStatutBadge = (statut) => {
    const styles = {
      nouveau: "bg-blue-100 text-blue-700",
      contacte: "bg-yellow-100 text-yellow-700",
      interesse: "bg-green-100 text-green-700",
      pas_interesse: "bg-red-100 text-red-700",
      rdv_pris: "bg-purple-100 text-purple-700",
      converti: "bg-emerald-100 text-emerald-700",
      inactif: "bg-gray-100 text-gray-500",
    };
    const labels = {
      nouveau: "Nouveau",
      contacte: "Contacté",
      interesse: "Intéressé",
      pas_interesse: "Pas intéressé",
      rdv_pris: "RDV pris",
      converti: "Converti",
      inactif: "Inactif",
    };
    return (
      <Badge className={styles[statut] || "bg-gray-100"}>
        {labels[statut] || statut}
      </Badge>
    );
  };

  // Create prospect
  const handleCreateProspect = async () => {
    if (!prospectForm.nom) {
      toast.error("Le nom est obligatoire");
      return;
    }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (editingProspect) {
        await axios.put(`${API}/api/prospection/prospects/${editingProspect.id}`, prospectForm, { headers });
        toast.success("Prospect mis à jour");
      } else {
        await axios.post(`${API}/api/prospection/prospects`, prospectForm, { headers });
        toast.success("Prospect créé");
      }
      setShowCreateProspectDialog(false);
      setProspectForm({ nom: "", prenom: "", email: "", telephone: "", entreprise: "", poste: "", ville: "", source: "", notes: "" });
      setEditingProspect(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  // Create liste
  const handleCreateListe = async () => {
    if (!listeForm.nom) {
      toast.error("Le nom est obligatoire");
      return;
    }
    try {
      await axios.post(`${API}/api/prospection/listes`, listeForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Liste créée");
      setShowCreateListeDialog(false);
      setListeForm({ nom: "", description: "", source: "" });
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la création");
    }
  };

  // Import CSV
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    if (filterListe !== "all") {
      formData.append("liste_id", filterListe);
    }

    try {
      const response = await axios.post(`${API}/api/prospection/import`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'import");
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setShowImportDialog(false);
  };

  // Add contact
  const handleAddContact = async () => {
    if (!contactForm.notes) {
      toast.error("Les notes sont obligatoires");
      return;
    }
    try {
      await axios.post(
        `${API}/api/prospection/prospects/${selectedProspectForContact.id}/contact`,
        contactForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Contact enregistré");
      setShowContactDialog(false);
      setContactForm({ type_contact: "appel", notes: "", resultat: "" });
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  // Delete prospect
  const handleDeleteProspect = async (id) => {
    if (!window.confirm("Supprimer ce prospect ?")) return;
    try {
      await axios.delete(`${API}/api/prospection/prospects/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Prospect supprimé");
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  // Convert to CRM
  const handleConvertToCRM = async (prospect) => {
    if (!window.confirm(`Convertir ${prospect.nom} en client CRM ?`)) return;
    try {
      await axios.post(
        `${API}/api/prospection/prospects/${prospect.id}/convert-to-crm`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Prospect converti en client CRM");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la conversion");
    }
  };

  // Bulk actions
  const handleBulkDelete = async () => {
    if (!window.confirm(`Supprimer ${selectedProspects.length} prospect(s) ?`)) return;
    try {
      await axios.post(
        `${API}/api/prospection/prospects/bulk-delete`,
        selectedProspects,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Prospects supprimés");
      setSelectedProspects([]);
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleBulkUpdateStatut = async (statut) => {
    try {
      await axios.post(
        `${API}/api/prospection/prospects/bulk-update-statut`,
        selectedProspects,
        { headers: { Authorization: `Bearer ${token}` }, params: { statut } }
      );
      toast.success("Statuts mis à jour");
      setSelectedProspects([]);
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  // Toggle selection
  const toggleSelectProspect = (id) => {
    setSelectedProspects(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedProspects([]);
    } else {
      setSelectedProspects(filteredProspects.map(p => p.id));
    }
    setSelectAll(!selectAll);
  };

  // Delete liste
  const handleDeleteListe = async (liste, deleteProspects = false) => {
    const msg = deleteProspects 
      ? `Supprimer la liste "${liste.nom}" et tous ses prospects ?`
      : `Supprimer la liste "${liste.nom}" ? Les prospects seront conservés.`;
    if (!window.confirm(msg)) return;
    
    try {
      await axios.delete(`${API}/api/prospection/listes/${liste.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { delete_prospects: deleteProspects }
      });
      toast.success("Liste supprimée");
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="prospection-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-black text-secondary flex items-center gap-2">
            <Target className="w-8 h-8 text-primary" />
            Prospection
          </h1>
          <p className="text-muted-foreground mt-1">
            Gérez vos listes de prospects (non synchronisé avec l'annuaire)
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowImportDialog(true)}>
            <Upload className="w-4 h-4 mr-2" />
            Importer
          </Button>
          <Button onClick={() => { setEditingProspect(null); setShowCreateProspectDialog(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Nouveau prospect
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-700">{stats.total}</p>
              <p className="text-xs text-blue-600">Total prospects</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-700">{stats.par_statut?.interesse || 0}</p>
              <p className="text-xs text-green-600">Intéressés</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-purple-700">{stats.par_statut?.rdv_pris || 0}</p>
              <p className="text-xs text-purple-600">RDV pris</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-emerald-700">{stats.par_statut?.converti || 0}</p>
              <p className="text-xs text-emerald-600">Convertis</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-yellow-700">{stats.par_statut?.contacte || 0}</p>
              <p className="text-xs text-yellow-600">Contactés</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-700">{stats.contacts_aujourdhui || 0}</p>
              <p className="text-xs text-amber-600">Contacts aujourd'hui</p>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="prospects" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Prospects ({prospects.length})
          </TabsTrigger>
          <TabsTrigger value="listes" className="flex items-center gap-2">
            <FolderOpen className="w-4 h-4" />
            Listes ({listes.length})
          </TabsTrigger>
        </TabsList>

        {/* Prospects Tab */}
        <TabsContent value="prospects" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterListe} onValueChange={setFilterListe}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filtrer par liste" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les listes</SelectItem>
                {listes.map(l => (
                  <SelectItem key={l.id} value={l.id}>{l.nom}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatut} onValueChange={setFilterStatut}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="nouveau">Nouveau</SelectItem>
                <SelectItem value="contacte">Contacté</SelectItem>
                <SelectItem value="interesse">Intéressé</SelectItem>
                <SelectItem value="pas_interesse">Pas intéressé</SelectItem>
                <SelectItem value="rdv_pris">RDV pris</SelectItem>
                <SelectItem value="converti">Converti</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Bulk actions */}
          {selectedProspects.length > 0 && (
            <div className="flex items-center gap-4 p-3 bg-primary/10 rounded-lg">
              <span className="text-sm font-medium">{selectedProspects.length} sélectionné(s)</span>
              <Button size="sm" variant="outline" onClick={() => handleBulkUpdateStatut("contacte")}>
                Marquer contacté
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleBulkUpdateStatut("interesse")}>
                Marquer intéressé
              </Button>
              <Button size="sm" variant="destructive" onClick={handleBulkDelete}>
                <Trash2 className="w-4 h-4 mr-1" />
                Supprimer
              </Button>
            </div>
          )}

          {/* Table */}
          <Card>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]">
                      <Checkbox
                        checked={selectAll}
                        onCheckedChange={toggleSelectAll}
                      />
                    </TableHead>
                    <TableHead>Nom</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Entreprise</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Contacts</TableHead>
                    <TableHead>Liste</TableHead>
                    <TableHead className="w-[80px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProspects.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        <Target className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p>Aucun prospect trouvé</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredProspects.map((prospect) => (
                      <TableRow key={prospect.id} className="hover:bg-muted/50">
                        <TableCell>
                          <Checkbox
                            checked={selectedProspects.includes(prospect.id)}
                            onCheckedChange={() => toggleSelectProspect(prospect.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{prospect.prenom} {prospect.nom}</p>
                            {prospect.poste && (
                              <p className="text-xs text-muted-foreground">{prospect.poste}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {prospect.email && (
                              <a href={`mailto:${prospect.email}`} className="flex items-center gap-1 text-sm text-blue-600 hover:underline">
                                <Mail className="w-3 h-3" />
                                {prospect.email}
                              </a>
                            )}
                            {prospect.telephone && (
                              <a href={`tel:${prospect.telephone}`} className="flex items-center gap-1 text-sm text-muted-foreground">
                                <Phone className="w-3 h-3" />
                                {prospect.telephone}
                              </a>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {prospect.entreprise && (
                            <div className="flex items-center gap-1">
                              <Building className="w-3 h-3 text-muted-foreground" />
                              {prospect.entreprise}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>{getStatutBadge(prospect.statut)}</TableCell>
                        <TableCell>
                          <span className="text-sm">{prospect.nombre_contacts || 0}</span>
                        </TableCell>
                        <TableCell>
                          {prospect.liste_id && (
                            <Badge variant="outline" className="text-xs">
                              {listes.find(l => l.id === prospect.liste_id)?.nom || "—"}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => {
                                setSelectedProspectForContact(prospect);
                                setShowContactDialog(true);
                              }}>
                                <PhoneCall className="w-4 h-4 mr-2" />
                                Ajouter contact
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => {
                                setSelectedProspectForHistory(prospect);
                                setShowHistoryDialog(true);
                              }}>
                                <History className="w-4 h-4 mr-2" />
                                Historique
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => {
                                setEditingProspect(prospect);
                                setProspectForm(prospect);
                                setShowCreateProspectDialog(true);
                              }}>
                                <Edit className="w-4 h-4 mr-2" />
                                Modifier
                              </DropdownMenuItem>
                              {prospect.statut !== "converti" && (
                                <DropdownMenuItem onClick={() => handleConvertToCRM(prospect)}>
                                  <ArrowRight className="w-4 h-4 mr-2" />
                                  Convertir en CRM
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                className="text-red-600"
                                onClick={() => handleDeleteProspect(prospect.id)}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        {/* Listes Tab */}
        <TabsContent value="listes" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowCreateListeDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle liste
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {listes.map((liste) => (
              <Card key={liste.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-lg">{liste.nom}</CardTitle>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => {
                          setFilterListe(liste.id);
                          setActiveTab("prospects");
                        }}>
                          <Users className="w-4 h-4 mr-2" />
                          Voir prospects
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          className="text-red-600"
                          onClick={() => handleDeleteListe(liste, false)}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Supprimer liste
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="text-red-600"
                          onClick={() => handleDeleteListe(liste, true)}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Supprimer avec prospects
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent>
                  {liste.description && (
                    <p className="text-sm text-muted-foreground mb-2">{liste.description}</p>
                  )}
                  <div className="flex items-center justify-between">
                    <Badge variant="secondary">
                      <Users className="w-3 h-3 mr-1" />
                      {liste.prospect_count} prospect(s)
                    </Badge>
                    {liste.source && (
                      <span className="text-xs text-muted-foreground">{liste.source}</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Créée le {format(new Date(liste.created_at), "dd/MM/yyyy", { locale: fr })}
                  </p>
                </CardContent>
              </Card>
            ))}
            
            {listes.length === 0 && (
              <div className="col-span-full text-center py-12 text-muted-foreground">
                <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Aucune liste de prospection</p>
                <Button variant="link" onClick={() => setShowCreateListeDialog(true)}>
                  Créer une liste
                </Button>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Prospect Dialog */}
      <Dialog open={showCreateProspectDialog} onOpenChange={setShowCreateProspectDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingProspect ? "Modifier le prospect" : "Nouveau prospect"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prénom</Label>
                <Input
                  value={prospectForm.prenom}
                  onChange={(e) => setProspectForm({...prospectForm, prenom: e.target.value})}
                />
              </div>
              <div>
                <Label>Nom *</Label>
                <Input
                  value={prospectForm.nom}
                  onChange={(e) => setProspectForm({...prospectForm, nom: e.target.value})}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={prospectForm.email}
                  onChange={(e) => setProspectForm({...prospectForm, email: e.target.value})}
                />
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input
                  value={prospectForm.telephone}
                  onChange={(e) => setProspectForm({...prospectForm, telephone: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Entreprise</Label>
                <Input
                  value={prospectForm.entreprise}
                  onChange={(e) => setProspectForm({...prospectForm, entreprise: e.target.value})}
                />
              </div>
              <div>
                <Label>Poste</Label>
                <Input
                  value={prospectForm.poste}
                  onChange={(e) => setProspectForm({...prospectForm, poste: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Ville</Label>
              <Input
                value={prospectForm.ville}
                onChange={(e) => setProspectForm({...prospectForm, ville: e.target.value})}
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={prospectForm.notes}
                onChange={(e) => setProspectForm({...prospectForm, notes: e.target.value})}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateProspectDialog(false)}>Annuler</Button>
            <Button onClick={handleCreateProspect}>
              {editingProspect ? "Mettre à jour" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Liste Dialog */}
      <Dialog open={showCreateListeDialog} onOpenChange={setShowCreateListeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle liste de prospection</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nom de la liste *</Label>
              <Input
                value={listeForm.nom}
                onChange={(e) => setListeForm({...listeForm, nom: e.target.value})}
                placeholder="Ex: Salon Pro 2026"
              />
            </div>
            <div>
              <Label>Source</Label>
              <Input
                value={listeForm.source}
                onChange={(e) => setListeForm({...listeForm, source: e.target.value})}
                placeholder="Ex: Salon, Achat fichier, LinkedIn..."
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={listeForm.description}
                onChange={(e) => setListeForm({...listeForm, description: e.target.value})}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateListeDialog(false)}>Annuler</Button>
            <Button onClick={handleCreateListe}>Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Importer des prospects</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Importez un fichier <strong>CSV</strong> ou <strong>Excel (.xlsx)</strong> avec les colonnes : 
              nom, prenom, email, telephone, entreprise, poste, ville
            </p>
            <div>
              <Label>Liste de destination (optionnel)</Label>
              <Select value={filterListe} onValueChange={setFilterListe}>
                <SelectTrigger>
                  <SelectValue placeholder="Aucune liste" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Aucune liste</SelectItem>
                  {listes.map(l => (
                    <SelectItem key={l.id} value={l.id}>{l.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              ref={fileInputRef}
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button onClick={() => fileInputRef.current?.click()} className="w-full">
              <Upload className="w-4 h-4 mr-2" />
              Sélectionner un fichier (CSV ou Excel)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Contact Dialog */}
      <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Ajouter un contact - {selectedProspectForContact?.nom}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Type de contact</Label>
              <Select 
                value={contactForm.type_contact} 
                onValueChange={(v) => setContactForm({...contactForm, type_contact: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="appel">Appel téléphonique</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="rdv">Rendez-vous</SelectItem>
                  <SelectItem value="relance">Relance</SelectItem>
                  <SelectItem value="note">Note</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes *</Label>
              <Textarea
                value={contactForm.notes}
                onChange={(e) => setContactForm({...contactForm, notes: e.target.value})}
                placeholder="Détails de l'échange..."
                rows={3}
              />
            </div>
            <div>
              <Label>Résultat</Label>
              <Select 
                value={contactForm.resultat} 
                onValueChange={(v) => setContactForm({...contactForm, resultat: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un résultat" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Aucun</SelectItem>
                  <SelectItem value="interesse">Intéressé</SelectItem>
                  <SelectItem value="pas_interesse">Pas intéressé</SelectItem>
                  <SelectItem value="rappeler">À rappeler</SelectItem>
                  <SelectItem value="rdv_pris">RDV pris</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContactDialog(false)}>Annuler</Button>
            <Button onClick={handleAddContact}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Historique - {selectedProspectForHistory?.prenom} {selectedProspectForHistory?.nom}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {selectedProspectForHistory?.historique_contacts?.length > 0 ? (
              selectedProspectForHistory.historique_contacts.map((entry, idx) => (
                <div key={entry.id || idx} className="p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <Badge variant="outline">{entry.type_contact}</Badge>
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(entry.date), "dd/MM/yyyy HH:mm", { locale: fr })}
                    </span>
                  </div>
                  <p className="text-sm">{entry.notes}</p>
                  {entry.resultat && (
                    <Badge className="mt-2" variant="secondary">{entry.resultat}</Badge>
                  )}
                  {entry.created_by_name && (
                    <p className="text-xs text-muted-foreground mt-1">Par: {entry.created_by_name}</p>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <History className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>Aucun contact enregistré</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistoryDialog(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
